import { useState, useEffect } from "react";
import { Modal } from "react-bootstrap";
import { Button, TextField, InputAdornment, IconButton } from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
/* The very same dialog the Messages page opens, on the same chat/teams
   endpoints — not a second copy of it. Chat teams and reporting teams are
   different records, but both are "who is on a team", so they are managed
   from the one page called Manage Teams. */
import TeamSetupDialog from '../Chat/TeamSetupDialog';
import { matchesSearch } from "../../helpers/search";
import { URLS } from "../../helpers/URLS";
import useAxios from "../../helpers/Hooks/useAxios";
import Select from 'react-select';
import SkeletonLoader from "../../loaders/Skeleton";
import NoData from "../../loaders/NoData";
import PaginationGlobal from "../TablePagination.js/PaginationGlobal";
import "../../assets/scss/tables.scss";

/* the columns this table shows, so the box finds a row by anything on it */
const SEARCH_FIELDS = [
    'employeeCode', 'employeeName', 'designation',
    'emailID', 'mobile', 'managerName',
];

const ManageTeams = () => {

    const { fetchData } = useAxios();

    const [teamsList, setTeamsList] = useState([]);

    const [pagination, setpagination] = useState([])

    const [rowsPerPage, setRowsPerPage] = useState(15);

    const [employeesList, setEmployeesList] = useState([]);

    const [teamModel, setTeamModel] = useState(false);

    const [loadSkeleton, setLoadSkeleton] = useState(true);

    const [pageNumber, setPageNumber] = useState(1);

    const [search, setSearch] = useState('');

    const [chatTeamsOpen, setChatTeamsOpen] = useState(false);

    /* Read from the same endpoint, so one answer decides who sees this on
       either page. Only an admin may read or write chat teams, and the
       button is not offered to anybody else. */
    const [canManageChatTeams, setCanManageChatTeams] = useState(false);

    const [teamData, setTeamData] = useState({
        companyName: "",
        countryCode: "",
        department: "",
        designation: "",
        emailID: "",
        employeeCode: "",
        employeeName: "",
        list: [],
        managerEmail: "",
        managerName: "",
        mobile: "",
        officeLocation: "",
        teamsList: [],
        workLocation: ""
    })

    const getEmployeesList = async () => {
        const resp = await fetchData({
            url: URLS.searchEmployees, method: 'post', body: {
                search: ""
            }
        });
        if (resp.status === 200) {
            setEmployeesList(resp.data);
        }
    }

    const getTeamsList = async () => {
        setLoadSkeleton(true);
        const resp = await fetchData({ url: URLS.getTeamsList, method: 'get' });
        if (resp.status === 200) {
            setTeamsList(resp.data)
            const count = Math.ceil(resp.data.length / rowsPerPage);
            const pageNumbers = [];
            for (let i = 0; i < count; i++) {
                pageNumbers.push(i + 1);
            }
            setpagination(pageNumbers);
            setLoadSkeleton(false);
        }
    }

    const getChatTeamAccess = async () => {
        const resp = await fetchData({ url: URLS.chatCategories, method: 'get' });
        if (resp.status === 200) {
            setCanManageChatTeams(Boolean(resp.data.canManageTeams));
        }
    }

    useEffect(() => {
        getTeamsList();
        getEmployeesList();
        getChatTeamAccess();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const teamMembersChange = async (value) => {
        try {
            setTeamData({
                ...teamData,
                list: value.map(x => {
                    return { employeeCode: x.employeeCode }
                })
            })
        } catch (err) {
            console.log("teamMembersChange Error:", err.message)
        }
    }

    const teamViewModel = () => setTeamModel(!teamModel);

    const filteredTeams = teamsList.filter((emp) => matchesSearch(emp, search, SEARCH_FIELDS));

    /* a narrower result set can leave you past the last page */
    useEffect(() => { setPageNumber(1); }, [search]);

    const getPageNo = (data) => {
        setPageNumber(data)
    }



    return (
        <div className='content wh-table'>
            <div className='wh-toolbar'>
                <h4 className='title'>Manage Teams</h4>

                <div className='wh-toolbar-controls'>
                    <TextField
                        placeholder='Search name, code, email, manager…'
                        size='small'
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className='wh-search-field'
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                            endAdornment: search
                                ? <InputAdornment position="end">
                                    <IconButton size='small' className='wh-search-clear'
                                        aria-label='Clear search' onClick={() => setSearch('')}>
                                        <CloseRoundedIcon fontSize='small' />
                                    </IconButton>
                                </InputAdornment>
                                : null,
                        }}
                    />

                    {/* "chat teams" rather than "teams": this page's table is
                        about who reports to whom, and the two must not read as
                        the same thing. */}
                    {canManageChatTeams && (
                        <button type="button" className="chat-manage"
                            onClick={() => setChatTeamsOpen(true)}>
                            <TuneRoundedIcon /> Manage chat teams
                        </button>
                    )}
                </div>
            </div>
            <div className="shadow-sm bg-white">
                <div className="card-body">
                    {
                        loadSkeleton ? <SkeletonLoader /> : (
                            filteredTeams.length > 0 ?
                                <div className="table-responsive wh-table-shell">
                                    <table className="table table-bordered">
                                        <thead>
                                            <tr>
                                                <th scope="col">Employee ID</th>
                                                <th scope="col">Employee Name</th>
                                                <th scope="col">Designation</th>
                                                <th scope="col">Email</th>
                                                <th scope="col">Mobile</th>
                                                <th scope="col">Manager Name</th>
                                                <th scope="col">Team List</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filteredTeams.slice((pageNumber - 1) * rowsPerPage,
                                                pageNumber * rowsPerPage).map((emp) => {
                                                    return <tr key={emp.employeeCode}>
                                                        <td scope="row">{emp.employeeCode}</td>
                                                        <td>{emp.employeeName}</td>
                                                        <td>{emp.designation}</td>
                                                        <td>{emp.emailID}</td>
                                                        <td>{emp.mobile}</td>
                                                        <td>{emp.managerName}</td>
                                                        <td>
                                                            <Button type="button" variant="outlined" onClick={(e) => {
                                                                setTeamData(emp)
                                                                teamViewModel()
                                                            }} className="me-2 team_button">Team ({emp.teamsList.length})</Button>
                                                        </td>
                                                    </tr>
                                                })}
                                        </tbody>
                                    </table>
                                    {filteredTeams.length > 0 ? <PaginationGlobal
                                        count={Math.ceil(filteredTeams.length / rowsPerPage)}
                                        pageNumber={pageNumber} func={getPageNo}
                                        rowsPerPage={rowsPerPage}
                                        onRowsPerPageChange={(size) => { setRowsPerPage(size); setPageNumber(1); }} /> : ""}
                                </div>
                                : <NoData />
                        )
                    }
                </div>
            </div>
            <Modal show={teamModel} onHide={teamViewModel} backdrop="static" size="lg" className="wh-modal">
                <Modal.Header closeButton>
                    <Modal.Title>Manage Team ({teamData.employeeName} - {teamData.employeeCode})</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className='col-md-12 row'>
                        <div className='col-md-12'>
                            <label htmlFor="exampleFormControlInput1" className="form-label">Team Members ({employeesList.filter(x => teamData.list.find(y => x.employeeCode === y.employeeCode)).length})</label>
                            <Select
                                isMulti
                                value={employeesList.filter(x => teamData.list.find(y => x.employeeCode === y.employeeCode))}
                                options={employeesList}
                                onChange={teamMembersChange}
                            />
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="contained" color="inherit" onClick={teamViewModel} className="me-3">
                        Close
                    </Button>
                    <Button onClick={async (e) => {
                        const resp = await fetchData({ url: URLS.manageTeam, method: 'post', body: teamData });
                        if (resp.status === 200) {
                            getTeamsList()
                            teamViewModel()
                        }
                    }} variant="contained" >
                        Save
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Nothing on this page's table depends on chat teams, so there is
                no onSaved reload to do — the dialog reloads itself. */}
            <TeamSetupDialog
                open={chatTeamsOpen}
                onClose={() => setChatTeamsOpen(false)}
            />
        </div>
    )
}

export default ManageTeams